<template>
  <div class="discount-code">
    <form
      class="discount-code__form form-group"
      :class="{
        'has-success': codeState === 'valid',
        'has-error': codeState === 'invalid',
      }"
      @submit.prevent="handleSubmit"
    >
      <div class="discount-code__input-container">
        <label class="form-group__label" for="discount-code-input">
          <transition name="fade">
            <icon-check-fill
              v-if="codeState === 'valid'"
              class="discount-code__state-icon"
            />
          </transition>

          <input
            id="discount-code-input"
            ref="input"
            v-model.trim="localCode"
            class="discount-code__input"
            autocapitalize="on"
            autocorrect="off"
            autocomplete="off"
            aria-autocomplete="none"
            name="code"
            :placeholder="$string('cart.code.input_placeholder')"
            type="text"
            @focus="checkErrorState"
          />
          <span class="discount-code__label">{{
            $string('cart.code.input_title')
          }}</span>
        </label>
      </div>

      <btn
        class="discount-code__submit"
        :label="$string('cart.code.button_title')"
        modifiers="secondary"
        name="discount-code-submit"
        :show-state-icon="codeState === 'loading'"
        type="submit"
      >
        <template #stateIcon>
          <icon-loading />
        </template>
      </btn>

      <p
        v-if="errorMessage"
        class="discount-code__error text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
      >
        <icon-warning-invert />
        {{ errorMessage }}
      </p>

      <div class="discount-code__code-list">
        <template v-for="discount of discountCodes" :key="discount.code">
          <div
            v-if="discount.applicable || cart.items.length === 0"
            class="discount-code__code-list-item"
          >
            <div class="discount-code__code-list-item__state">
              <icon-tag />
              <span
                class="discount-code__code-list-item__text"
                v-text="discount.code.toUpperCase()"
              />
              <button
                v-if="discount.code"
                class="discount-code__code-list-item__clear"
                type="button"
                @click="handleClear(discount.code)"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('accessibility.clear')"
                />
                <icon-close />
              </button>
            </div>
          </div>
        </template>
      </div>
    </form>
  </div>
</template>

<script>
/**
 * Vue: Discount code (cart-drawer)
 * -----------------------------------------------------------------------------
 * Discount code input.
 *
 * @param {Boolean} isActive - Cart drawer overlay is active.
 *
 */
import { mapActions, mapState } from 'pinia';

import Btn from '~global/btn/btn';

import IconCheckFill from '~icons/general/check-fill.svg';
import IconClose from '~icons/directional-navigation/close.svg';
import IconLoading from '~icons/general/loading.svg';
import IconWarningInvert from '~icons/general/warning-invert.svg';
import IconTag from '~icons/general/tag.svg';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useCartStore from '~stores/cart';

export default {
  name: 'DiscountCode',

  components: {
    Btn,
    IconCheckFill,
    IconClose,
    IconLoading,
    IconTag,
    IconWarningInvert,
  },

  data() {
    return {
      localCode: '',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      errorType: 'errorType',
      codeState: 'codeState',
      enteredCode: 'enteredCode',
    }),

    ...mapState(useCartStore, {
      cart: 'response',
    }),

    /**
     * Compute error message.
     * @returns {String}
     */
    errorMessage() {
      if (this.codeState !== 'invalid' || !this.errorType) {
        return '';
      }

      switch (this.errorType) {
        case 'combine':
          return this.$string('cart.code.errors.combine');
        case 'book':
          return this.$string('cart.code.errors.book');
        case 'deposit':
          return this.$string('cart.code.errors.deposit');
        case 'order':
          return this.$string('cart.code.errors.order');
        case 'expired':
          return this.$string('cart.code.errors.expired', {
            replace: {
              code: this.enteredCode || this.localCode,
            },
          });
        case 'invalid':
          return this.$string('cart.code.errors.invalid', {
            replace: {
              code: this.enteredCode || this.localCode,
            },
          });
        case 'market':
          return this.$string('cart.code.errors.market', {
            replace: {
              code: this.enteredCode || this.localCode,
            },
          });
        case 'internal':
        default:
          return this.$string('cart.code.errors.internal');
      }
    },

    discountCodes() {
      return this.cart.discount_codes || [];
    },
  },

  watch: {
    codeState() {
      if (this.codeState === 'ready') {
        this.localCode = '';
      }

      if (this.codeState !== 'valid') {
        this.$refs.input.blur();
        return;
      }
      setTimeout(() => {
        this.localCode = '';
        this.$refs.input.focus();
        this.setCodeState('ready');
        this.setErrorType('');
      }, 1000);
    },
  },

  methods: {
    ...mapActions(useDiscountCampaignsStore, {
      setEnteredCode: 'setEnteredCode',
      setRemoveCode: 'setRemoveCode',
      setCartItemPrices: 'setCartItemPrices',
      setCodeState: 'setCodeState',
      setErrorType: 'setErrorType',
    }),

    /**
     * On focus of input, reset the error message
     */
    checkErrorState() {
      this.setErrorType('');
      this.setCodeState('ready');
      this.localCode = '';
      this.setEnteredCode('');
    },

    /**
     * Handle clear button click.
     */
    handleClear(code) {
      this.localCode = '';
      this.$refs.input.focus();
      this.setRemoveCode(code, 'ready');
      this.setCartItemPrices([{}]);
    },

    /**
     * Handle form submit.
     */
    handleSubmit() {
      this.setEnteredCode(this.localCode?.toUpperCase());
    },
  },
};
</script>

<style lang="scss">
@import './discount-code';
</style>
